import { useEffect, useState, useCallback, useRef } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Activity, Search, Copy, Download, ChevronsDownUp, ChevronsUpDown, AlertTriangle, MessageCircleQuestion, CornerDownRight } from "lucide-react";

const HEARD = "/coach/heard";
const ERR_RE = /\b(error|fail|failed|failure|warn|warning|exception|denied|blocked)\b/i;
const LS_MODE = "kadana_stream_detail";     // summary | expanded
const LS_COLLAPSE = "kadana_stream_collapsed";

// Category 2: the agent's explanation stream (Patch 45 Part 3). Narration only — the BeCo Coach voice
// is rendered separately by BuilderTab so the two voices read as two distinct voices.
export default function StreamPanel({ projectId, mode, onJump }) {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(() => { try { return localStorage.getItem(LS_MODE) || "summary"; } catch { return "summary"; } });
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(LS_COLLAPSE) === "1"; } catch { return false; } });
  const [q, setQ] = useState("");
  const [sens, setSens] = useState(""); const [sensOpts, setSensOpts] = useState([]);
  const [recap, setRecap] = useState(null);
  const [ask, setAsk] = useState({});          // entryId -> question text
  const [answers, setAnswers] = useState({});   // entryId -> answer
  const [liveState, setLiveState] = useState("idle");
  const lastCount = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/process-log`);
      setData(data);
      const evs = data.events || [];
      const latest = evs[evs.length - 1];
      const fresh = latest && (Date.now() - new Date(latest.at).getTime() < 20000);
      setLiveState(evs.length > lastCount.current ? "building" : (fresh ? "building" : "idle"));
      lastCount.current = evs.length;
    } catch { /* empty until first build */ }
  }, [projectId]);

  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    api.get(`${HEARD}/sensitivity/${projectId}`).then(r => { setSens(r.data.sensitivity); setSensOpts(r.data.options || []); }).catch(() => {});
    api.get(`/builder/${projectId}/recap`).then(r => { if (r.data.new_events > 0) setRecap(r.data); api.post(`/builder/${projectId}/recap/seen`, {}).catch(() => {}); }).catch(() => {});
  }, [projectId]);

  const setDetailMode = (m) => { setDetail(m); try { localStorage.setItem(LS_MODE, m); } catch { /* ignore */ } };
  const toggleCollapse = () => setCollapsed(c => { const n = !c; try { localStorage.setItem(LS_COLLAPSE, n ? "1" : "0"); } catch { /* ignore */ } return n; });
  const setSensitivity = async (lvl) => {
    setSens(lvl);
    try { await api.put(`${HEARD}/sensitivity/${projectId}`, { level: lvl }); toast.success(`Coaching sensitivity: ${lvl}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copyEntry = (e) => {
    const line = `[${e.at}] ${e.step}: ${e.explanation || e.event_text}`;
    navigator.clipboard.writeText(line).then(() => toast.success("Entry copied")).catch(() => {});
  };
  const exportLog = () => {
    const lines = (data?.events || []).map(e => `[${e.at}] ${e.step} (${e.cost_running_credits}/${e.quote_credits} cr)\n  ${e.explanation || e.event_text}`);
    const blob = new Blob([`Kadana build session — project ${projectId}\n\n${lines.join("\n\n")}`], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `kadana-session-${projectId}.txt`; a.click();
  };
  const submitAsk = async (e) => {
    const question = (ask[e.id] || "").trim();
    if (!question) return;
    try {
      const { data } = await api.post("/builder/followup", { project_id: projectId, entry: `${e.step}: ${e.event_text}`, question });
      setAnswers(a => ({ ...a, [e.id]: data.answer }));
      setAsk(a => ({ ...a, [e.id]: undefined }));
      toast.success(`Answered · ${data.remaining} metered interactions left today`);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const jump = (e) => { onJump?.(e); toast.info(`Showing the preview as of ${String(e.at).slice(11, 19)}`); };

  const events = (data?.events || []).filter(e => !q || `${e.step} ${e.explanation} ${e.event_text}`.toLowerCase().includes(q.toLowerCase()));
  const stateMeta = { building: { c: "k-tag-info", t: "Building…" }, thinking: { c: "k-tag-warn", t: "Thinking…" }, idle: { c: "k-muted", t: "Idle" } }[liveState];

  if (collapsed) {
    return (
      <div className="k-container" data-testid="stream-collapsed" style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Activity size={14} color="var(--accent)" />
        <span className={stateMeta.c} data-testid="stream-live-status" style={{ fontSize: 12, fontWeight: 600 }}>{stateMeta.t}</span>
        <span className="k-muted" style={{ fontSize: 12 }}>{events.length} events</span>
        <button className="k-btn k-btn-ghost" data-testid="stream-expand" onClick={toggleCollapse} style={{ marginLeft: "auto", padding: "3px 8px" }}><ChevronsUpDown size={13} /> Expand</button>
      </div>
    );
  }

  return (
    <div className="k-container" data-testid="stream-panel" role="log" aria-live="polite" aria-label="Agent explanation stream" style={{ padding: 18, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <Activity size={16} color="var(--accent)" />
        <h3 className="k-heading" style={{ fontSize: 15, margin: 0 }}>What the agent is doing</h3>
        <span className={stateMeta.c} data-testid="stream-live-status" aria-live="polite" style={{ fontSize: 12, fontWeight: 600, marginLeft: 4 }}>● {stateMeta.t}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          <div style={{ display: "flex", border: "1px solid var(--control)", borderRadius: 8, overflow: "hidden" }} role="group" aria-label="Detail level">
            <button className={`k-btn ${detail === "summary" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="stream-detail-summary" onClick={() => setDetailMode("summary")} style={{ padding: "4px 10px", fontSize: 12 }}>Summary</button>
            <button className={`k-btn ${detail === "expanded" ? "k-btn-primary" : "k-btn-ghost"}`} data-testid="stream-detail-expanded" onClick={() => setDetailMode("expanded")} style={{ padding: "4px 10px", fontSize: 12 }}>Expanded</button>
          </div>
          <select className="k-control" data-testid="stream-sensitivity" aria-label="Coach sensitivity" value={sens} onChange={e => setSensitivity(e.target.value)} style={{ width: "auto", padding: "4px 6px", fontSize: 12 }}>
            {sensOpts.map(o => <option key={o} value={o}>Coach: {o}</option>)}
          </select>
          <button className="k-btn k-btn-ghost" data-testid="stream-export" onClick={exportLog} aria-label="Export session log" style={{ padding: "4px 8px" }}><Download size={13} /></button>
          <button className="k-btn k-btn-ghost" data-testid="stream-collapse" onClick={toggleCollapse} aria-label="Collapse stream" style={{ padding: "4px 8px" }}><ChevronsDownUp size={13} /></button>
        </div>
      </div>

      {recap && (
        <div className="k-control" data-testid="session-recap" style={{ marginBottom: 10, display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 13 }}><strong>Recap:</strong> {recap.message}</span>
          <button className="k-btn k-btn-ghost" data-testid="recap-dismiss" onClick={() => setRecap(null)} style={{ padding: "2px 8px" }}>Dismiss</button>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <Search size={13} className="k-muted" />
        <input className="k-control" data-testid="stream-search" aria-label="Search the stream" placeholder="Search what the agent has said…" value={q} onChange={e => setQ(e.target.value)} style={{ fontSize: 13 }} />
      </div>

      {events.length === 0 ? (
        <p className="k-muted" style={{ fontSize: 13 }} data-testid="stream-empty">No events yet. Run a build to see real pipeline events with a running cost.</p>
      ) : events.map(e => {
        const isErr = ERR_RE.test(`${e.step} ${e.event_text} ${e.explanation}`);
        return (
          <div key={e.id} data-testid={`stream-event-${e.step}`} className="k-control"
            style={{ marginBottom: 8, borderLeft: `3px solid ${isErr ? "var(--danger,#ef4444)" : "var(--accent)"}`, paddingLeft: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <span className="k-heading" style={{ fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                {isErr && <AlertTriangle size={13} className="k-tag-red" data-testid="stream-error-flag" aria-label="warning" />}
                <span className="k-muted" style={{ fontWeight: 600 }}>Agent</span> {e.step}
              </span>
              <span className="k-muted" style={{ fontSize: 11 }} title={e.at}>{String(e.at).slice(11, 19)} · {e.cost_running_credits}/{e.quote_credits} cr</span>
            </div>
            <div className="k-muted" style={{ fontSize: 13, marginTop: 4 }}>{e.explanation || e.event_text}</div>
            {detail === "expanded" && (
              <div className="k-muted" data-testid="stream-expanded-detail" style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>
                <span className="mono">step={e.step}</span> · <span className="mono">event={e.event_text}</span> · build {e.build_id || "—"}
              </div>
            )}
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button className="k-btn k-btn-ghost" data-testid="stream-jump" onClick={() => jump(e)} style={{ padding: "2px 8px", fontSize: 12 }}>Preview at this moment</button>
              <button className="k-btn k-btn-ghost" data-testid="stream-copy" onClick={() => copyEntry(e)} aria-label="Copy this entry" style={{ padding: "2px 8px" }}><Copy size={12} /></button>
              <button className="k-btn k-btn-ghost" data-testid="stream-ask" onClick={() => setAsk(a => ({ ...a, [e.id]: a[e.id] === undefined ? "" : undefined }))} style={{ padding: "2px 8px", fontSize: 12 }}><MessageCircleQuestion size={12} /> Ask</button>
            </div>
            {ask[e.id] !== undefined && (
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <input className="k-control" data-testid="stream-ask-input" autoFocus placeholder="Ask about this step (metered like a prompt)…" value={ask[e.id]} onChange={ev => setAsk(a => ({ ...a, [e.id]: ev.target.value }))} onKeyDown={ev => { if (ev.key === "Enter") submitAsk(e); }} style={{ fontSize: 13 }} />
                <button className="k-btn k-btn-primary" data-testid="stream-ask-send" onClick={() => submitAsk(e)} style={{ padding: "4px 10px" }}>Ask</button>
              </div>
            )}
            {answers[e.id] && (
              <div className="k-muted" data-testid="stream-answer" style={{ fontSize: 13, marginTop: 6, display: "flex", gap: 6 }}>
                <CornerDownRight size={13} style={{ flexShrink: 0, marginTop: 2 }} /><span>{answers[e.id]}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
